import { useContext, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '@/App';
import { ShoppingBag, LogOut, LayoutDashboard, Menu, X, User as UserIcon } from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';
import { isScavengerHuntVisible } from '@/utils/scavengerHunt';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_luxhemp/artifacts/eosdqbph_310C9C52-31F3-41A9-B491-B40A0F9BDE7A_L0_001-1_6_2026%2C%2012_08_40%20AM%20%281%29.png';

const LINKS = [
  { to: '/products', label: 'Shop' },
  { to: '/coas', label: 'Lab Tests' },
  { to: '/about', label: 'About' },
  { to: '/locations', label: 'Locations' },
  { to: '/contact', label: 'Contact' },
];

export default function Navbar() {
  const { user, logout, cart } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [huntVisible, setHuntVisible] = useState(() => isScavengerHuntVisible());

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!huntVisible) return;
    const id = setInterval(() => {
      const vis = isScavengerHuntVisible();
      setHuntVisible(vis);
      if (!vis) clearInterval(id);
    }, 60 * 1000);
    return () => clearInterval(id);
  }, [huntVisible]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileOpen(false);
  };

  const cartCount = cart?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  return (
    <>
      {/* Announcement bar - premium detail */}
      <div className="hidden md:block bg-gradient-to-r from-ha-green via-ha-green/95 to-ha-green text-ha-bg" data-testid="site-announcement">
        <div className="ha-container">
          <div className="flex items-center justify-between h-8 text-[10px] uppercase tracking-[0.32em] font-body font-bold">
            <span>Asheville, NC — Est. 2023</span>
            <div className="flex items-center gap-8">
              <span>Free pickup or shipping to your front door</span>
              <span>Lab-tested on every batch</span>
            </div>
          </div>
        </div>
      </div>

      <nav
        data-testid="site-navbar"
        className={`sticky top-0 z-40 transition-all duration-500 ${
          scrolled
            ? 'bg-ha-bg/92 backdrop-blur-2xl border-b border-ha-ink/12 shadow-[0_10px_40px_-30px_rgba(0,0,0,0.9)]'
            : 'bg-ha-bg/70 backdrop-blur-sm border-b border-transparent'
        }`}
      >
        <div className="ha-container">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Left: mobile menu */}
            <button
              onClick={() => setMobileOpen((o) => !o)}
              className="lg:hidden -ml-2 p-2 text-ha-ink hover:text-ha-green transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            {/* Left links - desktop */}
            <div className="hidden lg:flex items-center gap-10 flex-1">
              {LINKS.slice(0, 3).map((l) => {
                const active = location.pathname === l.to;
                return (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`nav-${l.label.toLowerCase().replace(' ', '-')}`}
                    data-active={active}
                    className={`ha-nav-link font-body text-[11px] tracking-[0.28em] uppercase font-semibold ${
                      active ? 'text-ha-ink' : 'text-ha-ink/75 hover:text-ha-green'
                    }`}
                  >
                    {l.label}
                  </Link>
                );
              })}
            </div>

            {/* Center logo - dominant, editorial */}
            <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 lg:absolute lg:left-1/2 lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2">
              <img
                src={LOGO_URL}
                alt="High Achievers"
                className={`w-auto transition-all duration-500 drop-shadow-[0_2px_10px_rgba(125,184,51,0.15)] ${scrolled ? 'h-11 md:h-14' : 'h-14 md:h-16'}`}
                loading="eager"
              />
            </Link>

            {/* Right links + actions */}
            <div className="hidden lg:flex items-center gap-10 flex-1 justify-end">
              {LINKS.slice(3).map((l) => {
                const active = location.pathname === l.to;
                return (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`nav-${l.label.toLowerCase().replace(' ', '-')}`}
                    data-active={active}
                    className={`ha-nav-link font-body text-[11px] tracking-[0.28em] uppercase font-semibold ${
                      active ? 'text-ha-ink' : 'text-ha-ink/75 hover:text-ha-green'
                    }`}
                  >
                    {l.label}
                  </Link>
                );
              })}
              {huntVisible && (
                <Link
                  to="/scavenger-hunt"
                  data-testid="nav-scavenger-hunt"
                  className="ha-nav-link font-body text-[11px] tracking-[0.28em] uppercase font-semibold text-ha-green"
                >
                  Hunt
                </Link>
              )}
            </div>

            {/* Right actions cluster */}
            <div className="flex items-center gap-1 lg:ml-8">
              {user ? (
                <>
                  <NotificationBell />
                  <Link
                    to={user.role === 'staff' ? '/staff/dashboard' : '/dashboard'}
                    data-testid="nav-dashboard"
                    className="p-2 text-ha-ink/80 hover:text-ha-green transition-colors"
                  >
                    <LayoutDashboard className="w-5 h-5" />
                  </Link>
                  <button
                    onClick={handleLogout}
                    data-testid="nav-logout"
                    className="hidden md:inline-flex p-2 text-ha-ink/80 hover:text-ha-green transition-colors"
                    aria-label="Log out"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="hidden md:inline-flex p-2 text-ha-ink/80 hover:text-ha-green transition-colors"
                  aria-label="Sign in"
                >
                  <UserIcon className="w-5 h-5" />
                </Link>
              )}
              <Link
                to="/cart"
                data-testid="nav-cart"
                className="relative p-2 text-ha-ink/85 hover:text-ha-green transition-colors"
                aria-label="Cart"
              >
                <ShoppingBag className="w-5 h-5" />
                {cartCount > 0 && (
                  <span
                    data-testid="nav-cart-count"
                    className="absolute top-0 right-0 bg-ha-green text-ha-bg text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center leading-none"
                  >
                    {cartCount}
                  </span>
                )}
              </Link>
            </div>
          </div>

          {/* Mobile drawer */}
          {mobileOpen && (
            <div className="lg:hidden border-t border-ha-ink/12 py-6 animate-in slide-in-from-top-2 duration-300 bg-ha-bg">
              <div className="flex flex-col">
                {LINKS.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(' ', '-')}`}
                    className="font-anton text-4xl uppercase text-ha-ink hover:text-ha-green transition-colors py-4 border-b border-ha-ink/8 tracking-wide"
                  >
                    {l.label}
                  </Link>
                ))}
                {huntVisible && (
                  <Link
                    to="/scavenger-hunt"
                    data-testid="mobile-nav-scavenger-hunt"
                    className="font-anton text-4xl uppercase text-ha-green py-4 border-b border-ha-ink/8 tracking-wide"
                  >
                    Scavenger Hunt
                  </Link>
                )}
                {!user ? (
                  <div className="flex gap-3 mt-6">
                    <Link to="/login" className="flex-1" data-testid="mobile-nav-login">
                      <button className="ha-btn ha-btn-ghost w-full">Sign In</button>
                    </Link>
                    <Link to="/signup" className="flex-1" data-testid="mobile-nav-signup">
                      <button className="ha-btn ha-btn-green w-full">Join</button>
                    </Link>
                  </div>
                ) : (
                  <button
                    onClick={handleLogout}
                    data-testid="mobile-nav-logout"
                    className="mt-6 ha-btn ha-btn-ghost w-full"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
}
